import React from "react";
import styled, { css } from "styled-components";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";

import { checkCodeExists } from "components/helper/callApi";
import Layout from "components/common/Layout";
import {
    formFrame,
    onBackgroundImage,
} from "components/common/backgroundFrame";
import { ValidateButton } from "components/common/ValidateButton";
import { Input } from "components/common/Input";
import ScanQRCodeButton from "components/qrCodeScanner/ScanQRCodeButton";

import { useBrowserSupported } from "hooks/useBrowserSupported";

import { CSS_VARIABLES } from "enums/cssVariables";
import { ERROR } from "types/snackBar.constant";

type Inputs = {
    sessionCode: string;
};

const HomePage = () => {
    const {
        register,
        handleSubmit,
        setValue,
        setError,
        resetField,
        formState: { errors, isValid, isSubmitting },
    } = useForm<Inputs>({
        mode: "onTouched",
    });

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const isBrowserSupported = useBrowserSupported();
    const showQRCodeScanner = isMobile;

    const onQRCodeScanned = (value: string) => {
        let sessionCode = value;
        if (sessionCode.includes("/")) {
            const parts = sessionCode.split("/");
            sessionCode = parts[parts.length - 1];
        }
        setValue("sessionCode", sessionCode.toUpperCase(), {
            shouldValidate: true,
        });
    };

    const onSubmit = async (inputs: Inputs) => {
        try {
            const res = await checkCodeExists(inputs.sessionCode);

            if (res.ok) {
                resetField("sessionCode");
                navigate(`/login/${inputs.sessionCode}`);
            } else {
                enqueueSnackbar(t(`errorCode.${window.env.REACT_APP_THEME}`), {
                    variant: ERROR,
                });
                setError("sessionCode", {
                    type: "manual",
                });
            }
        } catch {
            enqueueSnackbar(`${t("errorUnknown")}`, {
                variant: ERROR,
            });
        }
    };

    // Same rule as the personal code form: nothing typed, nothing to submit.
    const isSubmitDisabled = !isBrowserSupported || !isValid || isSubmitting;

    return (
        <>
            <Layout>
                <Wrapper>
                    <FormWrapper>
                        <FormTitle>{`${t("welcome")}`}</FormTitle>
                        <FormContent>
                            <p>
                                {t(`enterCode.${window.env.REACT_APP_THEME}`)}
                            </p>
                            <div>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <InputWithQRCodeWrapper>
                                        <StyledInput
                                            {...register("sessionCode", {
                                                required: true,
                                            })}
                                            $error={Boolean(errors.sessionCode)}
                                            placeholder={t(
                                                `codeOnline.${window.env.REACT_APP_THEME}`,
                                            )}
                                            aria-invalid={
                                                errors.sessionCode
                                                    ? "true"
                                                    : "false"
                                            }
                                            onChange={(
                                                e: React.FormEvent<HTMLInputElement>,
                                            ) => {
                                                const value =
                                                    e.currentTarget.value || "";
                                                // The custom onChange replaces the registered one: validate here so the button follows the input.
                                                setValue(
                                                    "sessionCode",
                                                    value.toUpperCase(),
                                                    { shouldValidate: true },
                                                );
                                            }}
                                            dir={i18n.dir(i18n.language)}
                                            disabled={!isBrowserSupported}
                                            style={{
                                                paddingRight: showQRCodeScanner
                                                    ? "35px"
                                                    : "12px",
                                            }}
                                        />
                                        {showQRCodeScanner && (
                                            <QRCodeButtonWrapper>
                                                <ScanQRCodeButton
                                                    setValue={onQRCodeScanned}
                                                />
                                            </QRCodeButtonWrapper>
                                        )}
                                    </InputWithQRCodeWrapper>
                                    <StyledButton
                                        type="submit"
                                        disabled={isSubmitDisabled}
                                    >
                                        {`${t("joinIn")}`}
                                    </StyledButton>
                                </form>
                            </div>
                        </FormContent>
                    </FormWrapper>
                </Wrapper>
            </Layout>
        </>
    );
};

const Wrapper = styled.div`
    padding: 0 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const FormWrapper = styled.div`
    text-align: center;
    background-color: #fafafa;
    box-shadow: 0px 0px 3px 0px rgb(0 0 0 / 35%);
    width: 320px;
    ${formFrame()}
`;

// Same title as the login page: the entity title color on the entity title background.
const FormTitle = styled.div`
    font-size: 1.3rem;
    padding: 1.25rem 1rem;
    color: var(${CSS_VARIABLES.PRIMARY_TEXT_COLOR});
    background-color: var(${CSS_VARIABLES.BACKGROUND_FORM_COLOR});
`;

const FormContent = styled.div`
    padding: 0 1.5rem 0.5rem 1.5rem;
    /* The title band ends right above: leave some room before the text. */
    ${onBackgroundImage(css`
        padding-top: 1.25rem;
    `)}
    > p {
        font-size: 0.9rem;
        line-height: 1.5rem;
        margin: 0;
        padding-bottom: 1rem;
    }
    > div {
        margin-bottom: 15px;
    }
`;

const InputWithQRCodeWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const QRCodeButtonWrapper = styled.div`
    position: absolute;
    right: 0;
    top: 0;
`;

const StyledInput = styled(Input)<{ $error: boolean }>`
    border-color: ${(props) => (props.$error ? "#dc3545" : null)};
    box-shadow: ${(props) =>
        props.$error ? "0 0 0 0.2rem rgb(220 53 69 / 25%)" : null};
    &:focus {
        border-color: ${(props) => (props.$error ? "#dc3545" : null)};
        box-shadow: ${(props) =>
            props.$error ? "0 0 0 0.2rem rgb(220 53 69 / 25%)" : null};
    }
`;

// Same disabled behavior as the personal code form button.
const StyledButton = styled(ValidateButton)`
    &:disabled {
        pointer-events: none;
    }
`;

export default HomePage;
